import {useEffect, useMemo} from 'react'
import {useQueryClient} from '@tanstack/react-query'

import {prefetchConfig} from './prefetch.config'
import {reactQueryFetchFunctionMap} from './prefetch.queryfn'
import {
  AdditionalQueryClientParamsType,
  AuthProviderEnum,
  CASBClassificationOptionsType,
  CASBFilesAPIParams,
  ClassificationTypes,
  ShareStatus,
} from 'interfaces'
import {checkPermission, usePermissions} from 'rbac'
import {CASBService} from 'services'
import {CASB_FILES_QUERY_KEY} from 'features/CASB/useCasbFilesQuery'
import {useFeatureFlag} from 'hooks'

/**
 * @description Helper object to generate query keys for CASB files based on provider and filters
 */
const CASBFilesQueryKeyGen = {
  base: [CASB_FILES_QUERY_KEY] as const,

  byProvider: (provider: AuthProviderEnum) =>
    [...CASBFilesQueryKeyGen.base, provider] as const,

  byProviderWithFilters: (
    provider: AuthProviderEnum,
    filters: CASBFilesAPIParams,
  ) => [...CASBFilesQueryKeyGen.base, provider, filters] as const,
}

const casbClassificationTypes = ['ip', 'pci', 'pii', 'other', 'phi']
const shareStatusTypes = ['PUBLIC', 'EXTERNAL']

interface UseGlobalPrefetchParams {
  shouldRun: boolean
  authProvider?: AuthProviderEnum
}

/**
 * @description A Hook to prefetch data on app mount
 * @param {UseGlobalPrefetchParams} params - Parameters to control prefetching
 * @param {boolean} params.shouldRun - Condition to determine if prefetching should run
 * @param {AuthProviderEnum} [params.authProvider] - The authentication provider to use
 * @returns void
 */
const useGlobalPrefetch = ({
  shouldRun,
  authProvider,
}: UseGlobalPrefetchParams) => {
  const queryClient = useQueryClient()
  const {permissions: userPermissions} = usePermissions()
  const {productionDopeFeatureFlag} = useFeatureFlag()

  const authProviderLowerCase = (authProvider ?? '')?.toLowerCase()

  /**
   * @description Generates additional query parameters for CASB files based on classifications and share status
   * @returns {Record<string, {provider: string; params: CASBFilesAPIParams}>} A map of query keys to parameters for CASB files API calls
   * @note This is used to prefetch CASB files with different classifications and share statuses
   */
  const casbFilesQueryClientParams = useMemo(() => {
    const queryParamsMap = new Map<
      string,
      {provider: string; params: CASBFilesAPIParams}
    >()

    if (productionDopeFeatureFlag) {
      for (const classification of casbClassificationTypes) {
        const queryKey = JSON.stringify(
          CASBFilesQueryKeyGen.byProviderWithFilters(
            authProviderLowerCase as AuthProviderEnum,
            {
              classifications: [classification as ClassificationTypes],
            },
          ),
        )

        queryParamsMap.set(queryKey, {
          provider: authProviderLowerCase,
          params: {
            classifications: [classification as CASBClassificationOptionsType],
          },
        })
      }

      for (const status of shareStatusTypes) {
        const queryKey = JSON.stringify(
          CASBFilesQueryKeyGen.byProviderWithFilters(
            authProviderLowerCase as AuthProviderEnum,
            {
              shareStatus: [status as ShareStatus],
            },
          ),
        )

        queryParamsMap.set(queryKey, {
          provider: authProviderLowerCase,
          params: {
            shareStatus: [status as ShareStatus],
          },
        })
      }
    }

    return {
      ...Object.fromEntries(queryParamsMap),
      [CASB_FILES_QUERY_KEY]: {
        provider: authProviderLowerCase,
      },
    }
  }, [authProviderLowerCase])

  const handleCASBFilesPrefetch = () => {
    Object.entries(casbFilesQueryClientParams).forEach(([queryKey, params]) => {
      const key =
        queryKey === CASB_FILES_QUERY_KEY
          ? [CASB_FILES_QUERY_KEY]
          : JSON.parse(queryKey).slice(0, 3)

      queryClient.prefetchInfiniteQuery(
        key,
        () => CASBService.getCASBFiles({...params}),
        {
          cacheTime: 1000 * 60 * 60 * 24, // 24 hours
        },
      )
    })
  }

  const additionalQueryClientParams: AdditionalQueryClientParamsType = {
    getCASBOverview: {provider: authProviderLowerCase},
  }

  useEffect(() => {
    /**
     * If shouldRun is not met, return
     * For example, if user is not logged in, we don't want to prefetch
     */
    if (!shouldRun) return

    // Global prefetch
    prefetchConfig.global.forEach(pageConfig => {
      const {queries, isReactQuery} = pageConfig
      if (isReactQuery)
        queries.forEach(queryConfig => {
          const {queryKey, queryFn, reqPermissions} = queryConfig
          const data = queryClient.getQueryData(queryKey)
          if (!data) {
            const fetchFunction = reactQueryFetchFunctionMap[queryFn]
            const params = additionalQueryClientParams[queryKey[0]]

            const hasPermission = checkPermission(
              reqPermissions,
              userPermissions,
            )
            if (!hasPermission) return

            /**
             * Don't prefetch CASB files.
             * The BE team has added lambda-snapstart, and we want to to check its impact without prefetching.
             * If we need to prefetch CASB files in the future, we can uncomment this functionality.
             */
            if (params) {
              fetchFunction(queryClient, params)
            } else if (queryKey[0] === CASB_FILES_QUERY_KEY) {
              handleCASBFilesPrefetch()
            } else {
              fetchFunction(queryClient)
            }
          }
        })
    })

    // ...future expansion for other scenarios
  }, [queryClient, shouldRun, userPermissions])
}

export default useGlobalPrefetch
